// Profile → Two-step sign-in. After the password, the super admin also enters a 6-digit code
// sent by email and/or text. Turning on texts to a new number first texts a code to that number.
// Backend: controllers/admin.js (getTwoFactor / setTwoFactor).

import { useEffect, useState } from "react";
import { get, post } from "../utils/apiManager.js";
import { toast } from "react-toastify";

const box = { background: '#fff', borderRadius: 14, border: '1px solid #edf0f7', boxShadow: '0 1px 4px rgba(26,39,68,.05)', overflow: 'hidden' };
const input = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 14, color: '#0a1628', background: '#fff', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
const label = { fontSize: 13, fontWeight: 600, color: '#3a4a6a', marginBottom: 6, display: 'block' };

const TwoFactorSettings = () => {
  const [state, setState] = useState(null); // saved: { email, sms, phone, accountEmail }
  const [email, setEmail] = useState(false);
  const [sms, setSms] = useState(false);
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [needsCode, setNeedsCode] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    get("/admin/two-factor").then((res) => {
      if (!res?.status) return;
      setState(res.data);
      setEmail(res.data.email); setSms(res.data.sms); setPhone(res.data.phone || "");
    });
  }, []);

  const changed = state && (email !== state.email || sms !== state.sms || (sms && phone !== state.phone));

  const save = () => {
    if (!password) { toast.error("Enter your current password"); return; }
    if (needsCode && !/^\d{6}$/.test(code.trim())) { toast.error("Enter the 6-digit code we texted you"); return; }
    setSaving(true);
    post("/admin/two-factor", { email, sms, phone, currentPassword: password, code: needsCode ? code.trim() : undefined })
      .then((res) => {
        setSaving(false);
        if (!res?.status) { toast.error(res?.message || "Could not save"); return; }
        if (res.needsPhoneCode) { setNeedsCode(true); toast.info(res.message); return; }
        toast.success(res.message);
        setState({ ...state, ...res.data });
        setPassword(""); setCode(""); setNeedsCode(false);
      })
      .catch(() => { setSaving(false); toast.error("Could not save"); });
  };

  if (!state) return null;
  const on = [state.email && 'email', state.sms && 'text message'].filter(Boolean);

  return (
    <div style={box}>
      <div style={{ padding: '18px 24px', borderBottom: '1px solid #edf0f7' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0a1628', margin: 0 }}>Two-step sign-in</h3>
        <p style={{ fontSize: 13, color: '#9aa5c0', margin: '2px 0 0' }}>
          {on.length ? `On: a code by ${on.join(' and ')} after your password.` : 'Off. Turn it on to also need a code when you sign in.'}
        </p>
      </div>
      <div style={{ padding: 24, display: 'grid', gap: 16 }}>
        <label style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 14, color: '#0a1628' }}>
          <input type="checkbox" checked={email} onChange={(e) => { setEmail(e.target.checked); setNeedsCode(false); }} />
          Email a code to {state.accountEmail}
        </label>
        <label style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 14, color: '#0a1628' }}>
          <input type="checkbox" checked={sms} onChange={(e) => { setSms(e.target.checked); setNeedsCode(false); }} />
          Text a code to my mobile phone
        </label>
        {sms && (
          <div>
            <span style={label}>Mobile number</span>
            <input style={input} type="tel" placeholder="(601) 555-0123" value={phone} onChange={(e) => { setPhone(e.target.value); setNeedsCode(false); }} />
          </div>
        )}
        {changed && (
          <div>
            <span style={label}>Current password</span>
            <input style={input} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
        )}
        {needsCode && (
          <div>
            <span style={label}>Code we texted to your phone</span>
            <input style={{ ...input, letterSpacing: 6, fontSize: 18 }} inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
          </div>
        )}
        <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>
          If a text can't be sent when you sign in and email is off, the code is emailed to {state.accountEmail} instead, so you can't be locked out.
        </p>
        {changed && (
          <div>
            <button type="button" onClick={save} disabled={saving}
              style={{ padding: '10px 22px', borderRadius: 8, border: 'none', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', background: '#AD2429', color: '#fff', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving...' : needsCode ? 'Confirm code and save' : 'Save'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoFactorSettings;
